import * as types from '../../../../constants/ActionNameConstants';
import { GLOSSARY_PAGE_SIZE, GLOSSARY_SORT_CYCLE, GLOSSARY_SORT_MODES } from '../../../../constants/GlossaryConstants';

// items: null means "never loaded" (show the loader); [] means "loaded and
// empty" (show the empty state). The two are never interchangeable.
const initialState = {
  preview: {
    loading: false,
    items: null,
    totalCount: 0,
    hasError: false,
    requestedForId: null,
  },
  list: {
    isOpen: false,
    loading: false,
    items: null,
    totalCount: 0,
    page: 1,
    search: '',
    sortMode: 'default',
    hasMore: true,
    hasError: false,
    requestedForId: null,
    retryToken: 0,
  },
};

// A genuine first load: nothing on screen yet, so the loader is right.
const freshListQuery = {
  items: null,
  page: 1,
  totalCount: 0,
  hasMore: true,
  hasError: false,
  loading: false,
};

// Search / sort change: the cursor resets but the current rows stay on screen
// until the new page 1 replaces them, so the modal never collapses to a loader
// and jumps. loading is set here rather than waiting for the request action —
// it keeps the NEXT_GLOSSARY_PAGE guard closed across the dispatch gap, when
// items still holds the previous (longer) list.
const refreshListQuery = {
  page: 1,
  hasMore: true,
  hasError: false,
  loading: true,
};

// Items without an Id are kept — they cannot be proven duplicates, and dropping
// them would lose data. Dupes within a single batch are caught too.
const mergeById = (existing, incoming) => {
  const seen = new Set();
  const merged = [];
  [...(existing || []), ...(incoming || [])].forEach((item) => {
    const key = item?.Id;
    if (key) {
      if (seen.has(key)) return;
      seen.add(key);
    }
    merged.push(item);
  });
  return merged;
};

// takeLatest cancels superseded requests, but a reset (route change, file
// change) cancels nothing — so a response can still arrive for a query the
// vendor has moved off. Compare query identity, never the page: the page is the
// field being consumed to decide replace-vs-append.
const isStaleListResponse = (state, params = {}) => {
  const sort = GLOSSARY_SORT_MODES[state.list.sortMode];
  return (
    params.audilfileId !== state.list.requestedForId ||
    params.search !== state.list.search ||
    params.sortRowName !== sort.sortRowName ||
    params.sortRowDirection !== sort.sortRowDirection
  );
};

const glossaryReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.GET_GLOSSARY_PREVIEW_REQUEST:
      return {
        ...state,
        preview: {
          ...state.preview,
          loading: true,
          hasError: false,
          requestedForId: action.payload?.audilfileId ?? null,
        },
      };

    case types.GET_GLOSSARY_PREVIEW_SUCCESS: {
      const { params, items, totalCount } = action.payload || {};
      if (params?.audilfileId !== state.preview.requestedForId) return state;
      return {
        ...state,
        preview: {
          ...state.preview,
          loading: false,
          hasError: false,
          items: items || [],
          totalCount: totalCount || (items || []).length,
        },
      };
    }

    case types.GET_GLOSSARY_PREVIEW_FAILED: {
      const { params } = action.payload || {};
      if (params?.audilfileId !== state.preview.requestedForId) return state;
      // items left untouched: a failure never wipes data already on screen.
      return { ...state, preview: { ...state.preview, loading: false, hasError: true } };
    }

    case types.OPEN_GLOSSARY_MODAL:
      return {
        ...state,
        list: {
          ...state.list,
          ...freshListQuery,
          isOpen: true,
          search: '',
          sortMode: 'default',
          requestedForId: action.payload ?? null,
        },
      };

    case types.CLOSE_GLOSSARY_MODAL:
      // Open is what resets, so closing leaves the list alone and nothing
      // flashes empty on the way out.
      return { ...state, list: { ...state.list, isOpen: false } };

    case types.SET_GLOSSARY_SEARCH: {
      // A debounce re-firing an unchanged value must not nuke a loaded list.
      if (state.list.search === action.payload) return state;
      return { ...state, list: { ...state.list, ...refreshListQuery, search: action.payload } };
    }

    case types.CYCLE_GLOSSARY_SORT: {
      const index = GLOSSARY_SORT_CYCLE.indexOf(state.list.sortMode);
      const nextMode = GLOSSARY_SORT_CYCLE[(index + 1) % GLOSSARY_SORT_CYCLE.length];
      return { ...state, list: { ...state.list, ...refreshListQuery, sortMode: nextMode } };
    }

    case types.NEXT_GLOSSARY_PAGE: {
      const list = state.list;
      if (!list.isOpen || !list.hasMore || list.loading || list.hasError) return state;
      // The observer can fire repeatedly before React re-renders, but the store
      // is always current here — so N hits produce exactly one increment.
      if ((list.items?.length || 0) < list.page * GLOSSARY_PAGE_SIZE) return state;
      return { ...state, list: { ...list, page: list.page + 1 } };
    }

    case types.RETRY_GLOSSARY_LIST:
      // Bumping the token is what re-triggers the fetch effect; page, search
      // and sort stay put so the failed request is the one retried.
      return {
        ...state,
        list: { ...state.list, hasError: false, hasMore: true, retryToken: state.list.retryToken + 1 },
      };

    case types.GET_GLOSSARY_LIST_REQUEST:
      return { ...state, list: { ...state.list, loading: true, hasError: false } };

    case types.GET_GLOSSARY_LIST_SUCCESS: {
      const { params, items, totalCount } = action.payload || {};
      if (isStaleListResponse(state, params)) return state;

      const batch = items || [];
      const merged = params.page === 1 ? batch : mergeById(state.list.items, batch);

      let hasMore;
      if (batch.length === 0) {
        hasMore = false;
      } else if (totalCount > 0) {
        hasMore = merged.length < totalCount;
      } else {
        // Count missing: fall back to "a full page probably means more". This
        // self-terminates on the first short page.
        hasMore = batch.length >= GLOSSARY_PAGE_SIZE;
      }

      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          hasError: false,
          items: merged,
          totalCount: totalCount || merged.length,
          hasMore,
        },
      };
    }

    case types.GET_GLOSSARY_LIST_FAILED: {
      const { params } = action.payload || {};
      if (isStaleListResponse(state, params)) return state;
      // hasMore false stops the sentinel retrying in a loop; items preserved so
      // a page-3 failure does not erase pages 1-2.
      return { ...state, list: { ...state.list, loading: false, hasError: true, hasMore: false } };
    }

    case types.RESET_GLOSSARY_STATE:
      return initialState;

    default:
      return state;
  }
};

export default glossaryReducer;
